import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Leaf, Sparkles, MapPin } from "lucide-react";
import DeckCalculator from "../components/DeckCalculator";
import useSEO from "../hooks/useSEO";
import { BUSINESS, LOCAL_BUSINESS_SCHEMA } from "../config/business";

const HERO_IMAGE =
  "https://images.pexels.com/photos/19135427/pexels-photo-19135427.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function HomePage() {
  useSEO({
    title:
      "Decking Cost Calculator Melbourne | Composite & Merbau Decking — Woodstone Construction",
    description:
      "Free decking cost calculator for Melbourne homeowners. Get an instant estimated price range for composite or merbau decking from Woodstone Construction — local deck builders in Melbourne.",
    canonical:
      typeof window !== "undefined"
        ? `${window.location.origin}/decking-cost-calculator-melbourne`
        : undefined,
    ogImage: HERO_IMAGE,
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  });

  return (
    <main data-testid="home-page">
      {/* ============================== HERO ============================== */}
      <section className="relative overflow-hidden">
        {/* Subtle grain / texture overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10 md:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white border border-[#DCD7CE] rounded-full pl-1 pr-4 py-1 text-[12px] text-[#4A4D4A]">
                <span className="bg-[#F3E8E5] text-[#7A2D2F] px-3 py-0.5 rounded-full font-semibold tracking-wide">
                  Melbourne, VIC
                </span>
                Composite · Merbau · Pergolas · Turf · Landscaping
              </div>

              <h1
                className="mt-6 text-4xl sm:text-5xl lg:text-[64px] text-[#1A1A1A] tracking-tight leading-[1.03] font-medium"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Decking&nbsp;Cost <br className="hidden sm:block" />
                <span className="text-[#7A2D2F]">Calculator</span> Melbourne.
              </h1>

              <p className="mt-5 text-lg text-[#4A4D4A] max-w-xl leading-relaxed">
                Get a quick estimated decking cost for your outdoor space.
                Melbourne-based decking and landscaping specialists.
              </p>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4A4D4A]">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#7A2D2F]" />
                  Instant price range
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#7A2D2F]" />
                  No sign-up required
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#7A2D2F]" />
                  Local Melbourne tradespeople
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-[#DCD7CE] shadow-[0_20px_50px_rgba(20,40,31,0.10)]">
                <img
                  src={HERO_IMAGE}
                  alt="Close-up of a modern composite deck with grooved boards against a timber-clad home"
                  className="w-full h-[280px] md:h-[360px] lg:h-[420px] object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-white/85 backdrop-blur rounded-2xl px-4 py-3 text-sm">
                  <div className="flex items-center gap-2 text-[#1A1A1A]">
                    <MapPin size={15} className="text-[#C9A876]" />
                    <span className="font-medium">Built across Melbourne</span>
                  </div>
                  <div className="text-[#4A4D4A]">Since day one</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== CALCULATOR ============================== */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <DeckCalculator />
        </div>
      </section>

      {/* ============================== SEO CONTENT ============================== */}
      <section className="bg-[#EFECE6] py-20 md:py-28 border-y border-[#DCD7CE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-3xl">
            <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-3">
              Decking cost calculator Melbourne · guide
            </div>
            <h2
              className="text-3xl md:text-5xl text-[#1A1A1A] tracking-tight leading-tight"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              Everything you need to know before you build.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
            <article className="prose prose-neutral max-w-none">
              <h3
                className="text-2xl text-[#1A1A1A] mb-3"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                How this decking calculator works
              </h3>
              <p className="text-[#4A4D4A] leading-relaxed">
                Our decking cost calculator Melbourne homeowners rely on is
                designed to be fast and honest. Choose your material —{" "}
                <strong>composite decking</strong> or{" "}
                <strong>merbau decking</strong> — enter your deck size either as
                length × width or total square metres, and we’ll instantly
                display an estimated project range. We use realistic local
                rates that include labour, materials and typical install
                work. The result is a guide only, not a fixed quote.
              </p>

              <h3
                className="text-2xl text-[#1A1A1A] mt-10 mb-3"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                What affects decking cost?
              </h3>
              <p className="text-[#4A4D4A] leading-relaxed">
                The final cost of your deck can shift based on several
                factors: site access, ground condition (soil vs existing
                concrete), deck height, steps, handrails, borders, raised
                framing, demolition of an old deck, and waste removal. Custom
                features like pergolas, built-in seating or privacy screens
                also change the price. For an accurate <em>decking quote
                Melbourne</em>, we’ll always measure on site.
              </p>
            </article>

            <article className="prose prose-neutral max-w-none">
              <h3
                className="text-2xl text-[#1A1A1A] mb-3"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Composite vs Merbau decking
              </h3>
              <p className="text-[#4A4D4A] leading-relaxed">
                <strong>Composite decking Melbourne</strong> is the
                low-maintenance choice — UV-stable, splinter-free, and it
                won’t need regular oiling. Great for busy families and high-sun
                backyards. <strong>Merbau decking Melbourne</strong> delivers
                that classic, warm Australian hardwood look with a deep,
                natural grain. Merbau costs slightly less upfront but needs
                occasional oiling to keep its colour. Both materials are long
                lasting when installed properly.
              </p>

              <h3
                className="text-2xl text-[#1A1A1A] mt-10 mb-3"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Why choose Woodstone Construction?
              </h3>
              <p className="text-[#4A4D4A] leading-relaxed">
                Woodstone Construction is a Melbourne-based deck builder
                specialising in composite and merbau decking, pergolas,
                artificial turf, and full landscaping makeovers. We focus on
                clean detailing, honest quoting, and tidy job sites. No
                middlemen, no bait pricing — just quality outdoor builds
                across Melbourne and its surrounding suburbs.
              </p>
            </article>
          </div>

          {/* trust pillars */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
            {[
              {
                icon: ShieldCheck,
                title: "Honest quoting",
                body: "Clear scope, clear numbers, no surprises on invoice day.",
              },
              {
                icon: Leaf,
                title: "Quality materials",
                body: "Long-lasting composite and premium merbau sourced from trusted suppliers.",
              },
              {
                icon: Sparkles,
                title: "Clean finish",
                body: "Neat framing, tidy sites, and a deck you’ll be proud to host on.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#DCD7CE] p-6"
                data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-xl bg-[#F3E8E5] flex items-center justify-center mb-4">
                  <Icon size={18} className="text-[#7A2D2F]" />
                </div>
                <div
                  className="text-lg text-[#1A1A1A] mb-1"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {title}
                </div>
                <div className="text-sm text-[#4A4D4A] leading-relaxed">
                  {body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== FINAL CTA ============================== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-[#0D0D0E] text-white p-10 md:p-16">
          <div
            aria-hidden
            className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#7A2D2F] blur-3xl opacity-60"
          />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-3">
                Ready when you are
              </div>
              <h2
                className="text-3xl md:text-5xl leading-tight tracking-tight"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Let’s build something you’ll love stepping onto.
              </h2>
              <p className="mt-4 text-[#BDBDB4] max-w-lg">
                Book a no-pressure site visit. We’ll measure your space, talk
                materials, and send through an accurate fixed quote.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#7A2D2F] rounded-full px-7 py-4 font-semibold hover:bg-[#F3E8E5] transition-all"
                data-testid="home-cta-request-quote"
              >
                Request exact quote <ArrowRight size={16} />
              </Link>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="inline-flex items-center justify-center gap-2 border border-white/30 rounded-full px-7 py-4 font-semibold hover:bg-white/10 transition-all"
                data-testid="home-cta-call"
              >
                Call {BUSINESS.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
